import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  CaretLeft, Buildings, Users as UsersIcon, ChartBar, Key, Plus, Copy, CheckCircle,
  PauseCircle, ClockCounterClockwise, ShieldCheck, CurrencyDollar, FileText, Note,
  Handshake, Gear, Warning, Trash, Receipt, DownloadSimple, Eye, X,
} from "@phosphor-icons/react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import api from "../lib/api";
import { formatApiError, formatDate } from "../lib/utils-extra";

const STATUS_COLOR = {
  active: "bg-[#16A34A]/10 text-[#16A34A]",
  trial: "bg-[#0EA5E9]/10 text-[#0EA5E9]",
  sponsored: "bg-[#0284C7]/10 text-[#0284C7]",
  internal_demo: "bg-[#475569]/10 text-[#475569]",
  suspended: "bg-[#D97706]/10 text-[#D97706]",
  archived: "bg-[#DC2626]/10 text-[#DC2626]",
};
const ROLE_COLOR = {
  owner: "bg-[#0EA5E9]/10 text-[#0EA5E9]",
  hr: "bg-[#0284C7]/10 text-[#0284C7]",
  manager: "bg-[#16A34A]/10 text-[#16A34A]",
  employee: "bg-[#475569]/10 text-[#475569]",
};

const TABS = [
  { id: "overview", label: "Overview", icon: ChartBar },
  { id: "general", label: "General Information", icon: Buildings },
  { id: "users", label: "Tenant Users", icon: UsersIcon },
  { id: "commercial", label: "Commercial Profile", icon: CurrencyDollar },
  { id: "deal", label: "Deal Notes", icon: Note },
  { id: "contract", label: "Contract Information", icon: Handshake },
  { id: "invoices", label: "Invoice History", icon: FileText },
  { id: "invoicegen", label: "Invoice Generator", icon: Receipt },
  { id: "activity", label: "Activity Timeline", icon: ClockCounterClockwise },
  { id: "audit", label: "Audit Summary", icon: ShieldCheck },
  { id: "settings", label: "Settings / Branding", icon: Gear },
];

export default function TenantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [activity, setActivity] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [tab, setTab] = useState("overview");
  const [confirmDelete, setConfirmDelete] = useState(false);

  const loadTenant = useCallback(() => api.get(`/tenants/${id}`).then(({ data }) => setTenant(data)), [id]);
  const loadUsers = useCallback(() => api.get(`/tenants/${id}/users`).then(({ data }) => setUsers(data)).catch(() => {}), [id]);
  const loadActivity = useCallback(() => api.get(`/tenants/${id}/activity`).then(({ data }) => setActivity(data)).catch(() => {}), [id]);
  const loadInvoices = useCallback(() => api.get(`/tenants/${id}/invoices`).then(({ data }) => setInvoices(data)).catch(() => {}), [id]);

  useEffect(() => {
    loadTenant();
    api.get(`/tenants/${id}/overview`).then(({ data }) => setOverview(data)).catch(() => {});
    loadUsers();
    loadActivity();
    loadInvoices();
  }, [id, loadTenant, loadUsers, loadActivity, loadInvoices]);

  const toggleStatus = async (newStatus) => {
    try {
      await api.put(`/tenants/${id}`, { status: newStatus });
      toast.success(`Tenant ${newStatus}`);
      loadTenant();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteTenant = async () => {
    try {
      await api.delete(`/tenants/${id}`);
      toast.success("Tenant deleted");
      navigate("/admin/tenants");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadWelcome = async () => {
    try {
      const res = await api.get(`/tenants/${id}/welcome-package/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `Welcome_Package_${(tenant?.name || "tenant").replace(/ /g, "_")}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!tenant) return <div className="text-sm text-[#475569]" data-testid="tenant-detail-loading">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="tenant-detail-page">
      {/* Header */}
      <div>
        <Link to="/admin/tenants" className="inline-flex items-center gap-1 text-sm text-[#475569] hover:text-[#0F172A] mb-3" data-testid="tenant-detail-back">
          <CaretLeft size={16} /> Tenants
        </Link>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-[#0EA5E9]/10 flex items-center justify-center">
              <Buildings size={26} weight="duotone" color="#0EA5E9" />
            </div>
            <div>
              <h1 className="font-display text-2xl sm:text-3xl font-semibold" data-testid="tenant-detail-name">{tenant.name}</h1>
              <div className="text-sm text-[#475569] mt-0.5">{tenant.industry || "—"} · {tenant.legal_business_name || "No legal name"}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={`${STATUS_COLOR[tenant.status] || STATUS_COLOR.active} border-0 capitalize`} data-testid="tenant-detail-status">
              {tenant.status?.replace("_", " ")}
            </Badge>
            <Button variant="outline" size="sm" onClick={downloadWelcome} data-testid="tenant-detail-welcome-package">
              <DownloadSimple size={14} className="mr-1" /> Welcome Package
            </Button>
            {tenant.status === "active" ? (
              <Button variant="outline" size="sm" onClick={() => toggleStatus("suspended")} data-testid="tenant-detail-suspend">
                <PauseCircle size={14} className="mr-1" /> Suspend
              </Button>
            ) : (
              <Button variant="outline" size="sm" onClick={() => toggleStatus("active")} data-testid="tenant-detail-activate">
                <CheckCircle size={14} className="mr-1" /> Activate
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => setConfirmDelete(true)} data-testid="tenant-detail-delete" className="text-[#DC2626] hover:text-[#DC2626]">
              <Trash size={14} className="mr-1" /> Delete
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs — horizontal scroll on mobile */}
      <div className="border-b border-[#E2E8F0] overflow-x-auto scrollbar-thin -mx-1 px-1">
        <div className="flex gap-1 min-w-max" data-testid="tenant-detail-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`tenant-tab-${t.id}`}
                className={`flex items-center gap-2 px-3 py-2.5 text-sm whitespace-nowrap border-b-2 transition-colors
                  ${active ? "border-[#0EA5E9] text-[#0EA5E9] font-medium" : "border-transparent text-[#475569] hover:text-[#0F172A]"}`}
              >
                <Icon size={16} weight={active ? "duotone" : "regular"} />
                {t.label}
                {t.phase2 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#F1F5F9] text-[#94A3B8]">P2</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab content */}
      <div data-testid={`tenant-panel-${tab}`}>
        {tab === "overview" && <OverviewPanel overview={overview} tenant={tenant} />}
        {tab === "general" && <GeneralPanel tenant={tenant} onSaved={loadTenant} />}
        {tab === "users" && <UsersPanel tenantId={id} users={users} reload={loadUsers} />}
        {tab === "commercial" && <CommercialPanel tenantId={id} />}
        {tab === "deal" && <DealNotesPanel tenantId={id} />}
        {tab === "contract" && <ContractPanel tenantId={id} />}
        {tab === "invoices" && <InvoiceHistoryPanel tenantId={id} invoices={invoices} reload={loadInvoices} onNew={() => setTab("invoicegen")} />}
        {tab === "invoicegen" && <InvoiceGeneratorPanel tenantId={id} tenant={tenant} onCreated={() => { loadInvoices(); loadActivity(); setTab("invoices"); }} />}
        {tab === "activity" && <ActivityPanel activity={activity} />}
        {tab === "audit" && <AuditPanel activity={activity} />}
        {tab === "settings" && <BrandingPanel tenant={tenant} onSaved={loadTenant} />}
      </div>

      {/* Delete tenant confirm */}
      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent className="max-w-md" data-testid="delete-tenant-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-[#DC2626]"><Trash size={20} /> Delete tenant</DialogTitle></DialogHeader>
          <DialogDescription className="text-sm text-[#475569]">
            This permanently deletes <span className="font-semibold">{tenant.name}</span> and ALL its data —
            users, employees, positions, KPI templates, evaluations, commercial profile, deal notes,
            contracts and invoices. This cannot be undone.
          </DialogDescription>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(false)}>Cancel</Button>
            <Button onClick={deleteTenant} data-testid="confirm-delete-tenant" className="bg-[#DC2626] hover:bg-[#B91C1C] text-white">
              Delete permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Overview ---------------- */
function Stat({ label, value, accent = "#0F172A" }) {
  return (
    <Card className="surface-card border-[#E2E8F0]">
      <CardContent className="p-4">
        <div className="small-label">{label}</div>
        <div className="font-display text-2xl font-semibold mt-1" style={{ color: accent }}>{value}</div>
      </CardContent>
    </Card>
  );
}
function OverviewPanel({ overview, tenant }) {
  if (!overview) return <div className="text-sm text-[#475569]">Loading…</div>;
  const c = overview.counts;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Active Employees" value={c.employees} accent="#0EA5E9" />
        <Stat label="Evaluations" value={c.evaluations} />
        <Stat label="Finalized" value={c.finalized} accent="#16A34A" />
        <Stat label="Avg Score" value={overview.avg_score != null ? `${overview.avg_score}%` : "—"} accent="#0284C7" />
        <Stat label="Tenant Users" value={c.users} />
        <Stat label="Positions" value={c.positions} />
        <Stat label="KPI Templates" value={c.templates} />
        <Stat label="Bonus Cycles" value={c.bonus_cycles} />
      </div>
      <Card className="surface-card border-[#E2E8F0]">
        <CardContent className="p-5">
          <div className="font-display text-lg font-semibold mb-3">Recent evaluations</div>
          {overview.recent_evaluations.length === 0 && <div className="text-sm text-[#475569]">No evaluations yet.</div>}
          {overview.recent_evaluations.map((e) => (
            <div key={e.id} className="flex items-center justify-between py-2.5 border-b border-[#E2E8F0] last:border-0">
              <div>
                <div className="font-medium text-sm">{e.employee_name}</div>
                <div className="text-xs text-[#475569]">{e.status} · {formatDate(e.evaluation_date)}</div>
              </div>
              <div className="font-mono-data text-sm">{e.final_score != null ? `${e.final_score}%` : "—"}</div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

/* ---------------- General Information ---------------- */
function GeneralPanel({ tenant, onSaved }) {
  const [form, setForm] = useState({
    name: tenant.name || "", legal_business_name: tenant.legal_business_name || "",
    industry: tenant.industry || "", contact_person: tenant.contact_person || "",
    contact_email: tenant.contact_email || "", contact_phone: tenant.contact_phone || "",
    status: tenant.status || "active", access_level: tenant.access_level || "basic",
    start_date: (tenant.start_date || "").slice(0, 10), expiry_date: (tenant.expiry_date || "").slice(0, 10),
    notes: tenant.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm({ ...form, [k]: v });

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.contact_email) delete payload.contact_email;
      if (!payload.expiry_date) payload.expiry_date = null;
      await api.put(`/tenants/${tenant.id}`, payload);
      toast.success("Tenant updated");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const field = (label, key, type = "text") => (
    <div>
      <Label className="small-label">{label}</Label>
      <Input type={type} className="mt-1.5" value={form[key]} onChange={(e) => set(key, e.target.value)} data-testid={`general-${key}`} />
    </div>
  );

  return (
    <Card className="surface-card border-[#E2E8F0]">
      <CardContent className="p-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {field("Tenant Name", "name")}
          {field("Legal Business Name", "legal_business_name")}
          {field("Industry", "industry")}
          {field("Contact Person", "contact_person")}
          {field("Contact Email", "contact_email", "email")}
          {field("Contact Phone", "contact_phone")}
          <div>
            <Label className="small-label">Status</Label>
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger className="mt-1.5" data-testid="general-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="trial">Trial</SelectItem>
                <SelectItem value="sponsored">Sponsored</SelectItem>
                <SelectItem value="internal_demo">Internal Demo</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="small-label">Access Level</Label>
            <Select value={form.access_level} onValueChange={(v) => set("access_level", v)}>
              <SelectTrigger className="mt-1.5" data-testid="general-access"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="basic">Basic</SelectItem>
                <SelectItem value="full_access">Full Access</SelectItem>
                <SelectItem value="sponsored_full_access">Sponsored Full Access</SelectItem>
                <SelectItem value="internal_demo">Internal Demo</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {field("Start Date", "start_date", "date")}
          {field("Expiry / Renewal Date", "expiry_date", "date")}
          <div className="sm:col-span-2">
            <Label className="small-label">Notes</Label>
            <Textarea rows={3} className="mt-1.5" value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="general-notes" />
          </div>
        </div>
        <div className="flex justify-end mt-5">
          <Button onClick={save} disabled={busy} data-testid="general-save" className="bg-[#0EA5E9] hover:bg-[#0284C7]">
            {busy ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/* ---------------- Tenant Users ---------------- */
function UsersPanel({ tenantId, users, reload }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "employee", password: "" });
  const [creds, setCreds] = useState(null);
  const [busy, setBusy] = useState(false);
  const [delUser, setDelUser] = useState(null);

  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Copied"); };

  const create = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      const { data } = await api.post(`/tenants/${tenantId}/users`, payload);
      setOpen(false); setForm({ name: "", email: "", role: "employee", password: "" });
      setCreds({ email: data.email, temp_password: data.temp_password });
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const reset = async (u) => {
    try {
      const { data } = await api.post(`/users/${u.id}/reset-password`, {});
      setCreds({ email: data.email, temp_password: data.temp_password });
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadWelcome = async (u) => {
    try {
      const res = await api.get(`/tenants/${tenantId}/welcome-package/pdf`, { params: { user_id: u.id }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `Welcome_Package_${(u.name || u.email || "user").replace(/ /g, "_")}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/users/${delUser.id}`);
      toast.success("User deleted");
      setDelUser(null);
      reload();
    } catch (e) { toast.error(formatApiError(e)); setDelUser(null); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-[#475569]">Users that belong to this tenant workspace.</div>
        <Button onClick={() => setOpen(true)} size="sm" data-testid="tenant-add-user" className="bg-[#0EA5E9] hover:bg-[#0284C7]">
          <Plus size={16} className="mr-1" /> Add user
        </Button>
      </div>
      <Card className="surface-card border-[#E2E8F0]">
        <CardContent className="p-0 divide-y divide-[#E2E8F0]">
          {users.map((u) => (
            <div key={u.id} className="flex flex-wrap items-center justify-between gap-3 p-4" data-testid={`tenant-user-${u.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#0EA5E9] to-[#0284C7] flex items-center justify-center text-white font-semibold text-sm shrink-0">
                  {u.name?.[0]?.toUpperCase() || "?"}
                </div>
                <div className="min-w-0">
                  <div className="font-medium truncate">{u.name}</div>
                  <div className="text-xs text-[#475569] truncate font-mono-data">{u.email}</div>
                  {u.must_change_password && (
                    <div className="text-[11px] text-[#D97706] mt-0.5 max-w-md" data-testid={`tenant-user-pending-note-${u.id}`}>
                      Temporary password issued. User may log in immediately and will be prompted to change their password.
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {u.must_change_password && (
                  <Badge className="bg-[#D97706]/10 text-[#D97706] border-0 gap-1" data-testid={`tenant-user-status-${u.id}`}><Warning size={12} /> First Login Pending</Badge>
                )}
                <Badge className={`${ROLE_COLOR[u.role] || ROLE_COLOR.employee} border-0 capitalize`}>{u.role}</Badge>
                <Button variant="outline" size="sm" onClick={() => downloadWelcome(u)} data-testid={`tenant-welcome-${u.id}`}>
                  <DownloadSimple size={14} className="mr-1" /> Welcome
                </Button>
                <Button variant="outline" size="sm" onClick={() => reset(u)} data-testid={`tenant-reset-${u.id}`}>
                  <Key size={14} className="mr-1" /> Reset
                </Button>
                <Button variant="outline" size="sm" onClick={() => setDelUser(u)} data-testid={`tenant-delete-user-${u.id}`} className="text-[#DC2626] hover:text-[#DC2626]">
                  <Trash size={14} />
                </Button>
              </div>
            </div>
          ))}
          {users.length === 0 && <div className="p-8 text-center text-sm text-[#475569]">No users in this tenant yet.</div>}
        </CardContent>
      </Card>

      {/* Add user */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Add tenant user</DialogTitle></DialogHeader>
          <DialogDescription className="text-sm text-[#475569]">A temporary password is generated; the user must change it at first login.</DialogDescription>
          <div className="space-y-3 py-1">
            <div><Label className="small-label">Name</Label><Input className="mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-user-name" /></div>
            <div><Label className="small-label">Email</Label><Input type="email" className="mt-1.5" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-user-email" /></div>
            <div>
              <Label className="small-label">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="mt-1.5" data-testid="new-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="owner">Owner</SelectItem>
                  <SelectItem value="hr">HR</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="employee">Employee</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label className="small-label">Password (blank = auto-generate)</Label><Input type="text" className="mt-1.5" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="new-user-password" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} disabled={busy || !form.name || !form.email} data-testid="save-new-user" className="bg-[#0EA5E9] hover:bg-[#0284C7]">{busy ? "Creating…" : "Create user"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credentials */}
      <Dialog open={!!creds} onOpenChange={(o) => !o && setCreds(null)}>
        <DialogContent className="max-w-md" data-testid="tenant-user-creds-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><CheckCircle size={20} weight="duotone" color="#16A34A" /> Temporary credentials</DialogTitle></DialogHeader>
          <DialogDescription className="text-sm text-[#475569]">Share securely. The user must change their password at next login.</DialogDescription>
          <div className="rounded-xl bg-[#F1F5F9] border border-[#E2E8F0] p-4 space-y-3">
            <div><div className="small-label mb-1">Email</div><div className="font-mono-data text-sm">{creds?.email}</div></div>
            <div>
              <div className="small-label mb-1">Temporary password</div>
              <div className="flex items-center gap-2">
                <code className="flex-1 font-mono-data text-sm bg-white border border-[#E2E8F0] rounded-lg px-3 py-2" data-testid="tenant-user-temp-password">{creds?.temp_password}</code>
                <Button variant="outline" size="sm" onClick={() => copy(creds?.temp_password)}><Copy size={14} /></Button>
              </div>
            </div>
          </div>
          <DialogFooter><Button onClick={() => setCreds(null)} className="bg-[#0EA5E9] hover:bg-[#0284C7]">Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete user confirm */}
      <Dialog open={!!delUser} onOpenChange={(o) => !o && setDelUser(null)}>
        <DialogContent className="max-w-md" data-testid="delete-user-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-[#DC2626]"><Trash size={20} /> Delete user</DialogTitle></DialogHeader>
          <DialogDescription className="text-sm text-[#475569]">
            Permanently delete <span className="font-semibold">{delUser?.email}</span>? They will lose access immediately. This cannot be undone.
          </DialogDescription>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelUser(null)}>Cancel</Button>
            <Button onClick={doDelete} data-testid="confirm-delete-user" className="bg-[#DC2626] hover:bg-[#B91C1C] text-white">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Activity Timeline ---------------- */
function ActivityPanel({ activity }) {
  if (!activity) return <div className="text-sm text-[#475569]">Loading…</div>;
  return (
    <Card className="surface-card border-[#E2E8F0]">
      <CardContent className="p-5">
        <div className="font-display text-lg font-semibold mb-3">Activity timeline</div>
        {activity.logs.length === 0 && <div className="text-sm text-[#475569]">No activity recorded.</div>}
        <div className="space-y-0">
          {activity.logs.map((l) => (
            <div key={l.id} className="flex items-start gap-3 py-2.5 border-b border-[#E2E8F0] last:border-0">
              <div className="w-2 h-2 rounded-full bg-[#0EA5E9] mt-2 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium capitalize">{l.action?.replace(/_/g, " ")}</div>
                <div className="text-xs text-[#475569]">
                  {l.user_email || "system"} {l.record_label ? `· ${l.record_label}` : ""}
                </div>
              </div>
              <div className="text-xs text-[#94A3B8] shrink-0">{formatDate(l.timestamp)}</div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/* ---------------- Audit Summary ---------------- */
function AuditPanel({ activity }) {
  if (!activity) return <div className="text-sm text-[#475569]">Loading…</div>;
  const entries = Object.entries(activity.summary).sort((a, b) => b[1] - a[1]);
  return (
    <Card className="surface-card border-[#E2E8F0]">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="font-display text-lg font-semibold">Audit summary</div>
          <Badge className="bg-[#0EA5E9]/10 text-[#0EA5E9] border-0">{activity.total} events</Badge>
        </div>
        {entries.length === 0 && <div className="text-sm text-[#475569]">No audit events.</div>}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {entries.map(([action, count]) => (
            <div key={action} className="flex items-center justify-between px-3 py-2 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0]">
              <span className="text-sm capitalize">{action.replace(/_/g, " ")}</span>
              <span className="font-mono-data text-sm font-semibold">{count}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/* ---------------- Branding ---------------- */
function BrandingPanel({ tenant, onSaved }) {
  const b = tenant.branding || {};
  const [form, setForm] = useState({
    product_name: b.product_name || "", company_display_name: b.company_display_name || "",
    login_tagline: b.login_tagline || "", primary_color: b.primary_color || "#0EA5E9",
    secondary_color: b.secondary_color || "#0284C7", logo_url: b.logo_url || "",
    pdf_footer_text: b.pdf_footer_text || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm({ ...form, [k]: v });

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/tenants/${tenant.id}`, { branding: form });
      toast.success("Branding updated");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Card className="surface-card border-[#E2E8F0]">
      <CardContent className="p-5 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><Label className="small-label">Product Name</Label><Input className="mt-1.5" value={form.product_name} onChange={(e) => set("product_name", e.target.value)} data-testid="brand-product-name" /></div>
          <div><Label className="small-label">Company Display Name</Label><Input className="mt-1.5" value={form.company_display_name} onChange={(e) => set("company_display_name", e.target.value)} data-testid="brand-company-name" /></div>
          <div className="sm:col-span-2"><Label className="small-label">Login Tagline</Label><Input className="mt-1.5" value={form.login_tagline} onChange={(e) => set("login_tagline", e.target.value)} data-testid="brand-tagline" /></div>
          <div>
            <Label className="small-label">Primary Color</Label>
            <div className="flex items-center gap-2 mt-1.5">
              <input type="color" value={form.primary_color} onChange={(e) => set("primary_color", e.target.value)} className="w-10 h-10 rounded-lg border border-[#E2E8F0]" data-testid="brand-primary-color" />
              <Input value={form.primary_color} onChange={(e) => set("primary_color", e.target.value)} className="font-mono-data" />
            </div>
          </div>
          <div>
            <Label className="small-label">Secondary Color</Label>
            <div className="flex items-center gap-2 mt-1.5">
              <input type="color" value={form.secondary_color} onChange={(e) => set("secondary_color", e.target.value)} className="w-10 h-10 rounded-lg border border-[#E2E8F0]" />
              <Input value={form.secondary_color} onChange={(e) => set("secondary_color", e.target.value)} className="font-mono-data" />
            </div>
          </div>
          <div><Label className="small-label">Logo URL</Label><Input className="mt-1.5" value={form.logo_url} onChange={(e) => set("logo_url", e.target.value)} data-testid="brand-logo-url" /></div>
          <div><Label className="small-label">PDF Footer Text</Label><Input className="mt-1.5" value={form.pdf_footer_text} onChange={(e) => set("pdf_footer_text", e.target.value)} data-testid="brand-pdf-footer" /></div>
        </div>
        <div className="flex justify-end">
          <Button onClick={save} disabled={busy} data-testid="brand-save" className="bg-[#0EA5E9] hover:bg-[#0284C7]">{busy ? "Saving…" : "Save branding"}</Button>
        </div>
      </CardContent>
    </Card>
  );
}

/* ---------------- Commercial Profile ---------------- */
function CommercialPanel({ tenantId }) {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/tenants/${tenantId}/commercial`).then(({ data }) => setForm(data)); }, [tenantId]);
  if (!form) return <div className="text-sm text-[#475569]">Loading…</div>;
  const set = (k, v) => setForm({ ...form, [k]: v });
  const save = async () => {
    setBusy(true);
    try {
      const { tenant_id, updated_at, updated_by, ...payload } = form;
      await api.put(`/tenants/${tenantId}/commercial`, payload);
      toast.success("Commercial profile saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const field = (label, key, type = "text") => (
    <div><Label className="small-label">{label}</Label>
      <Input type={type} className="mt-1.5" value={form[key] || ""} onChange={(e) => set(key, e.target.value)} data-testid={`commercial-${key}`} /></div>
  );
  return (
    <Card className="surface-card border-[#E2E8F0]"><CardContent className="p-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {field("Account Manager", "account_manager")}
        {field("Sold By", "sold_by")}
        {field("Deal Type", "deal_type")}
        <div>
          <Label className="small-label">Access Level</Label>
          <Select value={form.access_level || "basic"} onValueChange={(v) => set("access_level", v)}>
            <SelectTrigger className="mt-1.5" data-testid="commercial-access_level"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="basic">Basic</SelectItem>
              <SelectItem value="full_access">Full Access</SelectItem>
              <SelectItem value="sponsored_full_access">Sponsored Full Access</SelectItem>
              <SelectItem value="internal_demo">Internal Demo</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {field("Service Package", "service_package")}
        {field("Payment Terms", "payment_terms")}
        {field("Start Date", "start_date", "date")}
        {field("Renewal Date", "renewal_date", "date")}
        {field("Contract Status", "contract_status")}
        {field("Currency", "currency")}
        {field("Billing Contact", "billing_contact")}
        {field("Billing Email", "billing_email", "email")}
        {field("Billing Phone", "billing_phone")}
        <div className="sm:col-span-2"><Label className="small-label">Internal Notes</Label>
          <Textarea rows={3} className="mt-1.5" value={form.internal_notes || ""} onChange={(e) => set("internal_notes", e.target.value)} data-testid="commercial-internal_notes" /></div>
      </div>
      <div className="flex justify-end mt-5">
        <Button onClick={save} disabled={busy} data-testid="commercial-save" className="bg-[#0EA5E9] hover:bg-[#0284C7]">{busy ? "Saving…" : "Save commercial profile"}</Button>
      </div>
    </CardContent></Card>
  );
}

/* ---------------- Deal Notes ---------------- */
const DEAL_CATEGORIES = ["general", "discount", "staffing_bundle", "strategic_partnership", "sponsored", "revenue_share", "custom_pricing", "deal_change"];
function DealNotesPanel({ tenantId }) {
  const [notes, setNotes] = useState([]);
  const [note, setNote] = useState("");
  const [category, setCategory] = useState("general");
  const [busy, setBusy] = useState(false);
  const reload = useCallback(() => api.get(`/tenants/${tenantId}/deal-notes`).then(({ data }) => setNotes(data)), [tenantId]);
  useEffect(() => { reload(); }, [reload]);

  const add = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try {
      await api.post(`/tenants/${tenantId}/deal-notes`, { note, category });
      setNote(""); setCategory("general"); reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const remove = async (id) => {
    try { await api.delete(`/tenants/${tenantId}/deal-notes/${id}`); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4">
      <Card className="surface-card border-[#E2E8F0]"><CardContent className="p-5">
        <div className="text-sm text-[#475569] mb-3">Internal RMBC notes — discounts, bundles, partnerships, custom pricing. Timestamped &amp; audit-logged.</div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="sm:w-52" data-testid="deal-note-category"><SelectValue /></SelectTrigger>
            <SelectContent>{DEAL_CATEGORIES.map((c) => <SelectItem key={c} value={c} className="capitalize">{c.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
          </Select>
          <Input placeholder="Add an internal deal note…" value={note} onChange={(e) => setNote(e.target.value)} data-testid="deal-note-input" className="flex-1" />
          <Button onClick={add} disabled={busy || !note.trim()} data-testid="deal-note-add" className="bg-[#0EA5E9] hover:bg-[#0284C7]"><Plus size={16} className="mr-1" /> Add</Button>
        </div>
      </CardContent></Card>
      <Card className="surface-card border-[#E2E8F0]"><CardContent className="p-0 divide-y divide-[#E2E8F0]">
        {notes.map((n) => (
          <div key={n.id} className="flex items-start justify-between gap-3 p-4" data-testid={`deal-note-${n.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Badge className="bg-[#0EA5E9]/10 text-[#0EA5E9] border-0 capitalize">{n.category?.replace(/_/g, " ")}</Badge>
                <span className="text-xs text-[#94A3B8]">{n.author_name} · {formatDate(n.created_at)}</span>
              </div>
              <div className="text-sm">{n.note}</div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => remove(n.id)} data-testid={`deal-note-delete-${n.id}`} className="text-[#DC2626] hover:text-[#DC2626] shrink-0"><Trash size={14} /></Button>
          </div>
        ))}
        {notes.length === 0 && <div className="p-8 text-center text-sm text-[#475569]">No deal notes yet.</div>}
      </CardContent></Card>
    </div>
  );
}

/* ---------------- Contract Information ---------------- */
function ContractPanel({ tenantId }) {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/tenants/${tenantId}/contract`).then(({ data }) => setForm(data)); }, [tenantId]);
  if (!form) return <div className="text-sm text-[#475569]">Loading…</div>;
  const set = (k, v) => setForm({ ...form, [k]: v });
  const save = async () => {
    setBusy(true);
    try {
      const { tenant_id, updated_at, updated_by, documents, ...rest } = form;
      const payload = { ...rest, contract_value: rest.contract_value === "" ? null : Number(rest.contract_value) };
      await api.put(`/tenants/${tenantId}/contract`, payload);
      toast.success("Contract saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const field = (label, key, type = "text") => (
    <div><Label className="small-label">{label}</Label>
      <Input type={type} className="mt-1.5" value={form[key] ?? ""} onChange={(e) => set(key, e.target.value)} data-testid={`contract-${key}`} /></div>
  );
  return (
    <Card className="surface-card border-[#E2E8F0]"><CardContent className="p-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {field("Contract Number", "contract_number")}
        {field("Contract Status", "contract_status")}
        {field("Start Date", "start_date", "date")}
        {field("End Date", "end_date", "date")}
        {field("Renewal Date", "renewal_date", "date")}
        {field("Contract Value", "contract_value", "number")}
        <div className="sm:col-span-2"><Label className="small-label">Notes</Label>
          <Textarea rows={3} className="mt-1.5" value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} data-testid="contract-notes" /></div>
      </div>
      <div className="mt-4 rounded-xl border border-dashed border-[#CBD5E1] p-4 text-center text-sm text-[#94A3B8]" data-testid="contract-documents-placeholder">
        Document uploads coming soon (architecture ready)
      </div>
      <div className="flex justify-end mt-5">
        <Button onClick={save} disabled={busy} data-testid="contract-save" className="bg-[#0EA5E9] hover:bg-[#0284C7]">{busy ? "Saving…" : "Save contract"}</Button>
      </div>
    </CardContent></Card>
  );
}

/* ---------------- Invoice History ---------------- */
const INV_STATUS_COLOR = {
  draft: "bg-[#475569]/10 text-[#475569]", sent: "bg-[#0EA5E9]/10 text-[#0EA5E9]",
  partially_paid: "bg-[#D97706]/10 text-[#D97706]", paid: "bg-[#16A34A]/10 text-[#16A34A]",
  overdue: "bg-[#DC2626]/10 text-[#DC2626]", cancelled: "bg-[#94A3B8]/10 text-[#94A3B8]",
  void: "bg-[#DC2626]/10 text-[#DC2626]",
};
const INV_STATUSES = ["draft", "sent", "partially_paid", "paid", "overdue", "cancelled", "void"];
function fmtMoney(v, cur = "USD") {
  const sym = { USD: "$", EUR: "\u20ac", GBP: "\u00a3", PHP: "\u20b1" }[cur] || "";
  return `${sym}${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function InvoiceHistoryPanel({ tenantId, invoices, reload, onNew }) {
  const [detail, setDetail] = useState(null);

  const download = async (inv) => {
    try {
      const res = await api.get(`/tenants/${tenantId}/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${inv.invoice_number}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const changeStatus = async (inv, status) => {
    try { await api.put(`/tenants/${tenantId}/invoices/${inv.id}`, { status }); reload(); toast.success(`Marked ${status.replace("_", " ")}`); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (inv) => {
    try { await api.delete(`/tenants/${tenantId}/invoices/${inv.id}`); reload(); setDetail(null); toast.success("Invoice deleted"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-[#475569]">Manual invoices for this tenant.</div>
        <Button onClick={onNew} size="sm" data-testid="new-invoice-button" className="bg-[#0EA5E9] hover:bg-[#0284C7]"><Plus size={16} className="mr-1" /> New invoice</Button>
      </div>
      <Card className="surface-card border-[#E2E8F0]"><CardContent className="p-0 divide-y divide-[#E2E8F0]">
        {invoices.map((inv) => (
          <div key={inv.id} className="flex items-center justify-between gap-3 p-4" data-testid={`invoice-row-${inv.id}`}>
            <div className="min-w-0">
              <div className="font-medium font-mono-data text-sm">{inv.invoice_number}</div>
              <div className="text-xs text-[#475569]">{inv.issue_date} · Due {inv.due_date || "—"}</div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <div className="font-mono-data text-sm font-semibold">{fmtMoney(inv.total, inv.currency)}</div>
              <Badge className={`${INV_STATUS_COLOR[inv.status] || INV_STATUS_COLOR.draft} border-0 capitalize`}>{inv.status?.replace(/_/g, " ")}</Badge>
              <Button variant="ghost" size="sm" onClick={() => setDetail(inv)} data-testid={`invoice-view-${inv.id}`}><Eye size={16} /></Button>
              <Button variant="ghost" size="sm" onClick={() => download(inv)} data-testid={`invoice-pdf-${inv.id}`}><DownloadSimple size={16} /></Button>
            </div>
          </div>
        ))}
        {invoices.length === 0 && <div className="p-8 text-center text-sm text-[#475569]">No invoices yet. Use the Invoice Generator to create one.</div>}
      </CardContent></Card>

      {/* Invoice detail */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg" data-testid="invoice-detail-dialog">
          <DialogHeader><DialogTitle className="font-mono-data">{detail?.invoice_number}</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Invoice details</DialogDescription>
          {detail && (
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-[#475569]">Bill To</span><span className="font-medium">{detail.bill_to_name}</span>
              </div>
              <div className="rounded-lg border border-[#E2E8F0] divide-y divide-[#E2E8F0]">
                {detail.line_items?.map((li, i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-2 text-sm">
                    <span>{li.description} <span className="text-[#94A3B8]">× {li.quantity}</span></span>
                    <span className="font-mono-data">{fmtMoney(li.quantity * li.unit_price, detail.currency)}</span>
                  </div>
                ))}
              </div>
              <div className="text-sm space-y-1">
                <div className="flex justify-between"><span className="text-[#475569]">Subtotal</span><span className="font-mono-data">{fmtMoney(detail.subtotal, detail.currency)}</span></div>
                {detail.discount_amount > 0 && <div className="flex justify-between"><span className="text-[#475569]">Discount</span><span className="font-mono-data">-{fmtMoney(detail.discount_amount, detail.currency)}</span></div>}
                {detail.tax_amount > 0 && <div className="flex justify-between"><span className="text-[#475569]">Tax</span><span className="font-mono-data">{fmtMoney(detail.tax_amount, detail.currency)}</span></div>}
                <div className="flex justify-between font-semibold"><span>Total</span><span className="font-mono-data text-[#0EA5E9]">{fmtMoney(detail.total, detail.currency)}</span></div>
              </div>
              <div>
                <Label className="small-label">Status</Label>
                <Select value={detail.status} onValueChange={(v) => { changeStatus(detail, v); setDetail({ ...detail, status: v }); }}>
                  <SelectTrigger className="mt-1.5" data-testid="invoice-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{INV_STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => remove(detail)} data-testid="invoice-delete-button" className="text-[#DC2626] hover:text-[#DC2626] mr-auto"><Trash size={14} className="mr-1" /> Delete</Button>
            <Button variant="outline" onClick={() => download(detail)}><DownloadSimple size={14} className="mr-1" /> PDF</Button>
            <Button onClick={() => setDetail(null)} className="bg-[#0EA5E9] hover:bg-[#0284C7]">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Invoice Generator ---------------- */
function InvoiceGeneratorPanel({ tenantId, tenant, onCreated }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    branding_source: "rmbc", issue_date: today, due_date: "",
    bill_to_name: tenant?.name || "", bill_to_address: "", bill_to_email: tenant?.contact_email || "",
    discount: 0, discount_type: "amount", tax_rate: 0, currency: "USD",
    notes: "", payment_terms: "Net 30", status: "draft",
  });
  const [lines, setLines] = useState([{ description: "", quantity: 1, unit_price: 0 }]);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm({ ...form, [k]: v });
  const setLine = (i, k, v) => setLines(lines.map((l, idx) => idx === i ? { ...l, [k]: v } : l));
  const addLine = () => setLines([...lines, { description: "", quantity: 1, unit_price: 0 }]);
  const rmLine = (i) => setLines(lines.filter((_, idx) => idx !== i));

  const subtotal = lines.reduce((s, l) => s + Number(l.quantity || 0) * Number(l.unit_price || 0), 0);
  const discAmt = form.discount_type === "percent" ? subtotal * (Number(form.discount || 0) / 100) : Number(form.discount || 0);
  const taxable = Math.max(subtotal - Math.min(discAmt, subtotal), 0);
  const taxAmt = taxable * (Number(form.tax_rate || 0) / 100);
  const total = taxable + taxAmt;

  const save = async (status) => {
    setBusy(true);
    try {
      const payload = {
        ...form, status,
        line_items: lines.map((l) => ({ description: l.description, quantity: Number(l.quantity || 0), unit_price: Number(l.unit_price || 0) })),
        discount: Number(form.discount || 0), tax_rate: Number(form.tax_rate || 0),
        due_date: form.due_date || null,
      };
      await api.post(`/tenants/${tenantId}/invoices`, payload);
      toast.success("Invoice created");
      onCreated();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <Card className="surface-card border-[#E2E8F0]"><CardContent className="p-5 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div><Label className="small-label">Branding Source</Label>
            <Select value={form.branding_source} onValueChange={(v) => set("branding_source", v)}>
              <SelectTrigger className="mt-1.5" data-testid="invoice-branding-source"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="rmbc">RMBC</SelectItem>
                <SelectItem value="remediscore">RemediScore</SelectItem>
                <SelectItem value="per_tenant">Per Tenant</SelectItem>
              </SelectContent>
            </Select></div>
          <div><Label className="small-label">Issue Date</Label><Input type="date" className="mt-1.5" value={form.issue_date} onChange={(e) => set("issue_date", e.target.value)} data-testid="invoice-issue-date" /></div>
          <div><Label className="small-label">Due Date</Label><Input type="date" className="mt-1.5" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} data-testid="invoice-due-date" /></div>
          <div><Label className="small-label">Bill To Name</Label><Input className="mt-1.5" value={form.bill_to_name} onChange={(e) => set("bill_to_name", e.target.value)} data-testid="invoice-bill-name" /></div>
          <div><Label className="small-label">Bill To Email</Label><Input className="mt-1.5" value={form.bill_to_email} onChange={(e) => set("bill_to_email", e.target.value)} /></div>
          <div><Label className="small-label">Currency</Label><Input className="mt-1.5" value={form.currency} onChange={(e) => set("currency", e.target.value)} /></div>
          <div className="sm:col-span-3"><Label className="small-label">Bill To Address</Label><Input className="mt-1.5" value={form.bill_to_address} onChange={(e) => set("bill_to_address", e.target.value)} /></div>
        </div>

        {/* Line items */}
        <div>
          <div className="small-label mb-2">Line Items</div>
          <div className="space-y-2">
            {lines.map((l, i) => (
              <div key={i} className="flex gap-2 items-center" data-testid={`invoice-line-${i}`}>
                <Input placeholder="Description" value={l.description} onChange={(e) => setLine(i, "description", e.target.value)} data-testid={`invoice-line-desc-${i}`} className="flex-1" />
                <Input type="number" placeholder="Qty" value={l.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)} data-testid={`invoice-line-qty-${i}`} className="w-20" />
                <Input type="number" placeholder="Unit price" value={l.unit_price} onChange={(e) => setLine(i, "unit_price", e.target.value)} data-testid={`invoice-line-price-${i}`} className="w-28" />
                <div className="w-24 text-right font-mono-data text-sm">{fmtMoney(Number(l.quantity || 0) * Number(l.unit_price || 0), form.currency)}</div>
                <Button variant="ghost" size="sm" onClick={() => rmLine(i)} disabled={lines.length === 1} className="text-[#DC2626]"><X size={14} /></Button>
              </div>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={addLine} data-testid="invoice-add-line" className="mt-2"><Plus size={14} className="mr-1" /> Add line</Button>
        </div>

        {/* Discount / Tax / Terms */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div><Label className="small-label">Discount ({form.discount_type === "percent" ? "%" : (form.currency || "amount")})</Label><Input type="number" className="mt-1.5" value={form.discount} onChange={(e) => set("discount", e.target.value)} data-testid="invoice-discount" /></div>
          <div><Label className="small-label">Discount Type</Label>
            <Select value={form.discount_type} onValueChange={(v) => set("discount_type", v)}>
              <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="amount">Amount</SelectItem><SelectItem value="percent">Percent</SelectItem></SelectContent>
            </Select></div>
          <div><Label className="small-label">Tax Rate %</Label><Input type="number" className="mt-1.5" value={form.tax_rate} onChange={(e) => set("tax_rate", e.target.value)} data-testid="invoice-tax" /></div>
          <div><Label className="small-label">Payment Terms</Label><Input className="mt-1.5" value={form.payment_terms} onChange={(e) => set("payment_terms", e.target.value)} /></div>
        </div>
        <div><Label className="small-label">Notes</Label><Textarea rows={2} className="mt-1.5" value={form.notes} onChange={(e) => set("notes", e.target.value)} /></div>

        {/* Totals */}
        <div className="rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] p-4 ml-auto sm:w-72 text-sm space-y-1">
          <div className="flex justify-between"><span className="text-[#475569]">Subtotal</span><span className="font-mono-data" data-testid="gen-subtotal">{fmtMoney(subtotal, form.currency)}</span></div>
          {discAmt > 0 && <div className="flex justify-between"><span className="text-[#475569]">Discount</span><span className="font-mono-data">-{fmtMoney(Math.min(discAmt, subtotal), form.currency)}</span></div>}
          {taxAmt > 0 && <div className="flex justify-between"><span className="text-[#475569]">Tax</span><span className="font-mono-data">{fmtMoney(taxAmt, form.currency)}</span></div>}
          <div className="flex justify-between font-semibold pt-1 border-t border-[#E2E8F0]"><span>Total</span><span className="font-mono-data text-[#0EA5E9]" data-testid="gen-total">{fmtMoney(total, form.currency)}</span></div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => save("draft")} disabled={busy} data-testid="invoice-save-draft">Save as draft</Button>
          <Button onClick={() => save("sent")} disabled={busy} data-testid="invoice-save-sent" className="bg-[#0EA5E9] hover:bg-[#0284C7]">Create &amp; mark sent</Button>
        </div>
      </CardContent></Card>
    </div>
  );
}
